import * as THREE from 'three';
import { createStateMachine } from '../core/stateMachine.js';
import { createHealth } from '../core/health.js';
import { createStrikeLayer } from '../core/strikeLayer.js';
import { createCombo } from '../core/combo.js';
import { createMomentum } from '../core/momentum.js';
import { createHeat } from '../core/heat.js';
import { weaponMoves } from '../core/weapons.js';
import { createWeaponModel } from './weaponModel.js';
import { createLoadout } from '../core/styles.js';
import stateConfig from '../core/states.json';
import baseMoves from '../core/moves.json';
import { FEEL } from '../core/impact.js';
import { loadFighter, preloadFighter } from './loadFighter.js';
import { bindAnimation } from './bindAnimation.js';
import { createBoneVolumes } from './hitboxes.js';
import { createReachRings } from './reachRings.js';
import { HITBOXES } from './rigBoxes.js';
import { hurtboxesOf, reachOf } from './meshes.js';
import { normalizeBoneName } from './boneNames.js';

/**
 * One fighter, assembled: the rig, the state machine that governs it, its two
 * health layers, the volumes it strikes with and can be struck on, and the
 * strike layer that decides when a blow is live.
 *
 * Everything here already existed as a separate module; this is the seam they
 * meet at, so `main.js` does not have to know the order to wire six things in.
 *
 * `update()` poses the fighter — it deliberately does *not* advance the strike
 * layer. That is `fight.js`'s job, and it has to happen after every fighter in
 * the scene has been posed, or whoever was updated first would be punching at
 * where their opponent stood on the previous frame.
 */
/** What a blazin fighter glows. */
const BLAZIN_GLOW = 0x7a2a06;

/**
 * The tint on a textured fighter goes on their **clothes**, not their skin.
 *
 * Multiplying the whole material was tried first and it is the wrong tool: at
 * a strength where two vests read apart across a platform (0.8), the skin went
 * with them — crimson arms on one fighter, blue-grey on the other — and a
 * fighter's skin tone is not a team colour. So the texture is masked in the
 * shader: a texel takes the tint in proportion to how bright and how
 * unsaturated it is, which on a Meshy body is the cloth. Skin is saturated
 * (a mid brown is ~0.2 apart across its channels, linear) and dark cloth is
 * dark, so both fall outside the mask and keep their own colour. Values are in
 * linear light, which is what the sampler hands the shader.
 */
const CLOTH = Object.freeze({
  /** Luminance where the mask starts, and where it is full. */
  light: [0.3, 0.55],
  /** Saturation (max minus min channel) where the mask is full, and gone. */
  grey: [0.06, 0.18],
});

/**
 * The slots **painted** on the body (step 94), in the order the shader's
 * vectors hold them, and the bones each is painted from. Every body is skinned
 * to the same twenty bones, so a body's own skin weights say where its hands
 * and feet are — no mesh per tier, no second rig.
 *
 * Shins were painted too, on the front of the lower leg, and at fight-camera
 * distance it failed: on a body in loose trousers the paint landed on folded
 * cloth as an irregular patch that read as a stain, not a pad. Shins are the
 * one slot with geometry instead — see `SHIN_PAD` and `fitShinPads`.
 */
export const GEAR_REGIONS = Object.freeze([
  Object.freeze({ slot: 'hands', bones: ['lefthand', 'righthand'] }),
  Object.freeze({ slot: 'feet', bones: ['leftfoot', 'rightfoot'] }),
]);

/**
 * The shin pad: the smallest geometry that reads as one. A curved plate — an
 * open arc of a cylinder round the front of the lower leg — one per leg,
 * hung on the leg bone so it moves with it, sized to the body it is on.
 * `cover` is the stretch of the knee-to-ankle line it spans; `arc` how far
 * round the leg it wraps (radians); `stand` how far proud of the cloth it
 * sits, as a share of the leg's own radius.
 */
const SHIN_PAD = Object.freeze({ cover: [0.18, 0.86], arc: 2.6, stand: 0.14, segments: 12 });

/**
 * Where a body's shin pads go, read off the body itself in its bind pose: for
 * each leg, the knee and ankle (the leg and foot bones), and the radius of the
 * skin — trousers, on most of them — round the middle of the shin, measured to
 * the front, where a pad sits. Returned as each pad's leg bone and the plate's
 * transform **in that bone's space**, so it follows the leg through every clip.
 */
export function fitShinPads(object3D) {
  let mesh = null;
  object3D.traverse((child) => {
    if (!mesh && child.isSkinnedMesh && child.skeleton) mesh = child;
  });
  if (!mesh) return [];
  const bones = mesh.skeleton.bones;
  const find = (name) => bones.findIndex((bone) => normalizeBoneName(bone.name) === name);
  const position = mesh.geometry.getAttribute('position');
  const skinIndex = mesh.geometry.getAttribute('skinIndex');
  const skinWeight = mesh.geometry.getAttribute('skinWeight');
  if (!position || !skinIndex || !skinWeight) return [];
  // Facing, at bind: every body is rigged facing +Z (gotcha 175).
  const forward = new THREE.Vector3(0, 0, 1).transformDirection(mesh.bindMatrix);
  const pads = [];
  for (const side of ['left', 'right']) {
    const leg = find(`${side}leg`);
    const foot = find(`${side}foot`);
    if (leg < 0 || foot < 0) continue;
    const at = (index) => new THREE.Vector3().setFromMatrixPosition(new THREE.Matrix4().copy(mesh.skeleton.boneInverses[index]).invert());
    const knee = at(leg);
    const ankle = at(foot);
    const axis = ankle.clone().sub(knee);
    const length = axis.length();
    if (!(length > 0)) continue;
    axis.normalize();
    const front = forward.clone().sub(axis.clone().multiplyScalar(forward.dot(axis))).normalize();
    const across = new THREE.Vector3().crossVectors(axis, front).normalize();
    let depth = 0;
    let width = 0;
    const v = new THREE.Vector3();
    for (let i = 0; i < position.count; i += 1) {
      let w = 0;
      for (let k = 0; k < 4; k += 1) if (skinIndex.getComponent(i, k) === leg) w += skinWeight.getComponent(i, k);
      if (w < 0.5) continue;
      v.fromBufferAttribute(position, i).applyMatrix4(mesh.bindMatrix).sub(knee);
      const t = v.dot(axis) / length;
      if (t < 0.3 || t > 0.7) continue;
      const radial = v.sub(axis.clone().multiplyScalar(v.dot(axis)));
      depth = Math.max(depth, radial.dot(front));
      width = Math.max(width, Math.abs(radial.dot(across)));
    }
    if (!(depth > 0)) continue;
    const radius = Math.max(depth, width) * (1 + SHIN_PAD.stand);
    const [from, to] = SHIN_PAD.cover;
    const centre = knee.clone().add(axis.clone().multiplyScalar(length * (from + to) / 2));
    // The plate's own frame: y along the leg (up, knee-wards), z out the front.
    const up = axis.clone().negate();
    const basis = new THREE.Matrix4().makeBasis(new THREE.Vector3().crossVectors(up, front), up, front).setPosition(centre);
    const inBone = new THREE.Matrix4().copy(mesh.skeleton.boneInverses[leg]).multiply(basis);
    pads.push({ bone: bones[leg], matrix: inBone, radius, height: length * (to - from) });
  }
  return pads;
}

/**
 * Puts a `gearMask` on every skinned geometry in a body: per vertex, how much
 * of it the hand and foot bones carry (a `vec2`). Computed once per
 * geometry — two fighters in one body share it, and it is the same answer.
 */
export function markGear(object3D) {
  object3D.traverse((child) => {
    if (!child.isSkinnedMesh || !child.geometry || child.geometry.getAttribute('gearMask')) return;
    const bones = child.skeleton?.bones ?? [];
    const regionOf = bones.map((bone) => {
      const name = normalizeBoneName(bone.name);
      return GEAR_REGIONS.findIndex((region) => region.bones.includes(name));
    });
    const index = child.geometry.getAttribute('skinIndex');
    const weight = child.geometry.getAttribute('skinWeight');
    const count = child.geometry.getAttribute('position').count;
    const width = GEAR_REGIONS.length;
    const mask = new Float32Array(count * width);
    if (index && weight) {
      for (let v = 0; v < count; v += 1) {
        for (let k = 0; k < 4; k += 1) {
          const region = regionOf[index.getComponent(v, k)] ?? -1;
          if (region >= 0) mask[v * width + region] += weight.getComponent(v, k);
        }
      }
    }
    child.geometry.setAttribute('gearMask', new THREE.BufferAttribute(mask, width));
  });
}

/**
 * Hooks the cloth tint and the gear into a fighter material, once, and
 * returns the uniforms both live in. Every fighter's material compiles to the
 * same program; only the uniforms differ.
 *
 * The gear is a **paint** over the slot's region: the tier's colour, shaded by
 * the texture underneath so the folds stay, and its own roughness and
 * metalness — so gold reads as gold and tape as tape at five hundred pixels a
 * metre, which is the only distance it has to work at.
 */
function dress(material) {
  const tint = { value: new THREE.Color(0xffffff) };
  const gear = {
    gearColour: { value: [new THREE.Color(), new THREE.Color()] },
    gearOn: { value: new THREE.Vector2() },
    gearMetal: { value: new THREE.Vector2() },
    gearRough: { value: new THREE.Vector2(1, 1) },
  };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.clothTint = tint;
    Object.assign(shader.uniforms, gear);
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'attribute vec2 gearMask;\nvarying vec2 vGear;\nvoid main() {')
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>\n  vGear = gearMask;`);
    shader.fragmentShader = shader.fragmentShader
      .replace(
        'void main() {',
        `uniform vec3 clothTint;
uniform vec3 gearColour[2];
uniform vec2 gearOn;
uniform vec2 gearMetal;
uniform vec2 gearRough;
varying vec2 vGear;
void main() {
  vec2 gearW = smoothstep(vec2(${GEAR_PAINT.edge[0]}), vec2(${GEAR_PAINT.edge[1]}), vGear) * gearOn;`
      )
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
#ifdef USE_MAP
{
  vec3 t = sampledDiffuseColor.rgb;
  float light = dot(t, vec3(0.2126, 0.7152, 0.0722));
  float spread = max(max(t.r, t.g), t.b) - min(min(t.r, t.g), t.b);
  float cloth = smoothstep(${CLOTH.light[0]}, ${CLOTH.light[1]}, light)
    * (1.0 - smoothstep(${CLOTH.grey[0]}, ${CLOTH.grey[1]}, spread));
  diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * clothTint, cloth);
}
#endif
{
  float shade = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
  shade = ${GEAR_PAINT.shade[0]} + ${GEAR_PAINT.shade[1]} * clamp(shade, 0.0, 1.0);
  for (int i = 0; i < 2; i++) {
    diffuseColor.rgb = mix(diffuseColor.rgb, gearColour[i] * shade, gearW[i]);
  }
}`
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
  roughnessFactor = mix(mix(roughnessFactor, gearRough.x, gearW.x), gearRough.y, gearW.y);`
      )
      .replace(
        '#include <metalnessmap_fragment>',
        `#include <metalnessmap_fragment>
  metalnessFactor = mix(mix(metalnessFactor, gearMetal.x, gearW.x), gearMetal.y, gearW.y);`
      );
  };
  material.customProgramCacheKey = () => 'fighter-dress';
  material.needsUpdate = true;
  return { tint, gear };
}

/**
 * How the paint is laid on. `edge`: the bone weight where a slot's paint
 * starts and where it is full (a soft edge at the wrist and the ankle). `shade`:
 * how much of the texture's own light and dark survives under the paint.
 * `metal`: metalness is scaled by this, because the arenas light fighters with
 * lamps and no environment map, and full metal with nothing to reflect is
 * black.
 */
const GEAR_PAINT = Object.freeze({
  edge: [0.35, 0.7],
  shade: [0.65, 0.7],
  metal: 0.55,
});

/**
 * The move set every fighter's strike layer is built from: the twelve the
 * styles teach, plus the one each weapon grants.
 *
 * Merged here rather than in `moves.json` so a weapon stays one entry in
 * `weapons.json`, and so the "sorted by startup, damage strictly increases"
 * invariant from step 31 keeps meaning what it says — that rule is about the
 * hand-to-hand set trading commitment for damage, and a bat is not in that
 * conversation.
 *
 * Every fighter knows *of* every weapon move. What stops them throwing one is
 * the same gate that stops them throwing a move their styles never taught:
 * `knows()`, below.
 */
const moveConfig = Object.freeze({
  ...baseMoves,
  moves: Object.freeze({ ...baseMoves.moves, ...weaponMoves() }),
});

export async function createCombatant({
  id,
  modelUrl,
  fallbackUrl = null,
  animationUrls = [],
  position = [0, 0, 0],
  tint = null,
  /**
   * How a mesh file from `meshes.json` becomes a URL, and which file is the
   * default body. Without them the fighter can still be built, but cannot
   * change bodies — `wear` refuses.
   */
  meshUrl = null,
  defaultMesh = null,
  /** How fast this fighter earns momentum. See `momentum.js`. */
  charisma = 1,
  /**
   * Styles, most important first. The primary supplies the KO condition and
   * the bulk of the loadout; anything after it only widens the move list and
   * softens the stats.
   */
  styles = ['streetfighting'],
  onWarn = (message) => console.warn(`[${id}] ${message}`),
} = {}) {
  /**
   * The body. A `let`, because the slot changes who is standing in it and
   * different people have different skeletons (fighter-a and fighter-b do not
   * share a single bone position) — see `wear`. `object3D` does not change:
   * it is the group the scene, the arena host and the camera hold, and each
   * body is built inside it.
   */
  let fighter = await loadFighter({ name: id, modelUrl, fallbackUrl, animationUrls, onWarn });
  const object3D = fighter.object3D;
  object3D.position.set(...position);
  // Always cloned, tint or no tint. Two fighters built from the same GLB share
  // one material otherwise, and the blazin glow would light up both of them.
  let materials = cloneMaterials(object3D);
  let baseColours = materials.map((m) => m.color?.clone() ?? null);
  markGear(object3D);
  let dressed = materials.map((m) => dress(m));
  let tintHex = null;
  /** What is painted on hands, feet and shins: `{ slot: look }` (see `bank.looksOf`). */
  let gearLooks = {};

  /**
   * The shin pads: two plates on the leg bones of the body being worn,
   * refitted on every change of body, shown only when shins are worn.
   */
  let pads = [];
  const padMaterial = new THREE.MeshStandardMaterial({ side: THREE.DoubleSide });
  function fitPads(body) {
    for (const pad of pads) pad.removeFromParent();
    for (const pad of pads) pad.geometry.dispose();
    pads = fitShinPads(body).map(({ bone, matrix, radius, height }) => {
      const geometry = new THREE.CylinderGeometry(radius, radius, height, SHIN_PAD.segments, 1, true, -SHIN_PAD.arc / 2, SHIN_PAD.arc);
      const pad = new THREE.Mesh(geometry, padMaterial);
      pad.name = 'shin-pad';
      pad.matrixAutoUpdate = false;
      pad.matrix.copy(matrix);
      pad.castShadow = true;
      bone.add(pad);
      return pad;
    });
  }
  fitPads(object3D);

  /** Puts the gear's uniforms on every material of the body being worn. */
  function applyGear(next = gearLooks) {
    gearLooks = next ?? {};
    const shins = gearLooks.shins ?? null;
    for (const pad of pads) pad.visible = Boolean(shins);
    if (shins) {
      padMaterial.color.set(shins.colour);
      padMaterial.metalness = (shins.metal ?? 0) * GEAR_PAINT.metal;
      padMaterial.roughness = shins.rough ?? 1;
    }
    for (const { gear } of dressed) {
      GEAR_REGIONS.forEach((region, i) => {
        const look = gearLooks[region.slot] ?? null;
        gear.gearOn.value.setComponent(i, look ? 1 : 0);
        if (!look) return;
        gear.gearColour.value[i].set(look.colour);
        gear.gearMetal.value.setComponent(i, (look.metal ?? 0) * GEAR_PAINT.metal);
        gear.gearRough.value.setComponent(i, look.rough ?? 1);
      });
    }
    return gearLooks;
  }
  /**
   * Colours the fighter. Two fighters wearing one mesh have to be told apart
   * at a glance, and the colour comes off the roster entry through
   * `bookFighter`, so it changes with whoever is booked into the slot.
   *
   * A textured material takes it on its cloth only — see `CLOTH`. The
   * untextured mannequin (the fallback) has no texture to keep and takes the
   * tint whole, as it always did. `null` puts the file's own colours back.
   */
  function applyTint(next) {
    tintHex = next == null ? null : new THREE.Color(next).getHex();
    materials.forEach((material, i) => {
      if (material.map) {
        dressed[i].tint.value.set(tintHex ?? 0xffffff);
        return;
      }
      if (!material.color || !baseColours[i]) return;
      material.color.copy(baseColours[i]);
      if (tintHex !== null) material.color.set(tintHex);
    });
    return tintHex;
  }
  applyTint(tint);

  const machine = createStateMachine(stateConfig);
  const health = createHealth();
  const animation = bindAnimation(machine, fighter);
  // The move set is cross-checked against the state graph here, because this is
  // the one place that holds both. A move naming a state the graph has never
  // heard of fails at boot rather than the first time somebody throws it.
  const strikes = createStrikeLayer(moveConfig, {
    knownStates: Object.keys(stateConfig.states),
  });

  const hitboxes = createBoneVolumes(fighter, HITBOXES, {
    color: 0xffd166,
    name: `${id}-hit`,
    onWarn,
  });
  // Fitted to this body by `measure-clips.mjs --bodies --write`, or the
  // mannequin's own for a body the manifest does not know (the fallback).
  const hurtboxes = createBoneVolumes(fighter, hurtboxesOf(fighter.source), {
    color: 0x4dd0ff,
    name: `${id}-hurt`,
    onWarn,
  });

  // Parented to the fighter, so the rings ride them without anything needing to
  // be updated every frame.
  const reach = createReachRings(moveConfig.moves, { name: `${id}-reach` });
  object3D.add(reach.object3D);

  // Leaving a strike drops it, whatever the reason. A fighter knocked out of
  // their own punch by a stronger one does not get to finish it, and the clip
  // speed the move asked for must not be left behind on the next animation.
  machine.onChange(({ from, to }) => {
    if (strikes.active && from === strikes.state && to !== from) {
      strikes.cancel();
      fighter.animator?.setTimeScale(1);
    }
  });

  /**
   * Seconds left on the punish window opened by a missed strike.
   *
   * It starts the moment the strike's window shuts empty, which is while the
   * fighter is still locked in their own recovery — that overlap is the whole
   * mechanic. Taking a hit spends it: the punish is the one blow, not the whole
   * combo that follows.
   */
  let exposed = 0;
  /** Seconds left reeling off a hard surface. See `stun` below. */
  let reeling = 0;
  /**
   * What this fighter has been taught. A `let` rather than a `const` because
   * styles can be swapped mid-round from the debug overlay — everything that
   * reads it does so through the closure, so a swap takes effect on the next
   * frame with nothing to rebuild.
   */
  /**
   * The fighter's own deviation from their styles. Survives a style swap,
   * because it is a property of the person rather than of what they fight.
   */
  let spread = null;
  let loadout = createLoadout(styles, { spread });
  /**
   * What to call them.
   *
   * **Not the id.** `id` is the slot — "player", "opponent" — and everything
   * that matches a hit to a fighter keys on it, so it must not move when the
   * person standing in the slot changes. The name is what a human reads, and
   * it changes every time somebody new walks out.
   */
  let name = id;
  // The combo layer asks before it offers a route, so a fighter is never
  // shown a follow-up into a move they do not know.
  const combo = createCombo(moveConfig, { knows: (name) => combatant.knows(name) });
  /**
   * What this fighter is holding, or null.
   *
   * At most one. A weapon is an *instance* rather than a kind — it carries the
   * swings it has left — and the same object moves between fighters when one is
   * reversed off somebody.
   */
  let weapon = null;
  const armListeners = new Set();
  // What it looks like. Parented to the hand bone, so it rides the animation.
  const weaponModel = createWeaponModel(fighter, { onWarn });
  /** Grapples survive a weapon; strikes do not. Asked by name, once. */
  const isGrapple = (name) => moveConfig.moves[name]?.kind === 'grapple';

  /**
   * The stun gate: why a move that wants a reeling target may not be thrown.
   *
   * `requiresStun` was declared on the wall slam from the moment the slam
   * existed and read by **nobody**. The rule was real, but it lived in
   * `playerControl` as "offer the finisher when the opponent is stunned, and
   * the clinch otherwise" — which *chooses* a move rather than refusing one, so
   * every other way of asking for a slam went straight past it: the overlay's
   * move buttons, the dummy, a buffered combo follow-up, anything holding
   * `window.__FIGHTGAME__`.
   *
   * It lives here now, because `throwStrike` is the single door every one of
   * those goes through, and it reads the **flag** rather than naming the move —
   * so a second move that wants a reeling target needs no code at all.
   *
   * Fails closed. A move that requires a target to be reeling cannot be thrown
   * when there is no target to ask about: "I could not tell" is not a reason to
   * allow the strongest move in the game.
   */
  function stunRefusal(move) {
    if (!move?.requiresStun) return null;
    if (!opponent) return `${move.name} needs a reeling target, and nobody is squared up`;
    if (!opponent.stunned) return `${move.name} needs ${opponent.id ?? 'them'} to be reeling`;
    return null;
  }
  /** Who this fighter is squared up against. Set once, after both are built. */
  let opponentId = null;
  /**
   * And the fighter themselves, when we were handed one rather than a name.
   *
   * Only the stun gate needs the object: a move that declares `requiresStun`
   * has to ask whether the fighter opposite is actually reeling, and an id
   * cannot answer that.
   */
  let opponent = null;
  /**
   * How worth watching this fighter is. Momentum says they are winning; heat
   * says anybody would want to see it. `against` is a function because who they
   * are facing is not decided when they are built — `main.js` squares them up
   * afterwards — and every heat event carries an opponent.
   */
  const heat = createHeat({ id, against: () => opponentId });
  /**
   * And heat drives the meter. Wired here rather than at each `gain()` call
   * site, so nothing downstream can forget it: every path that earns momentum —
   * a blow, a counter, a guard, a taunt — goes through the same multiplier.
   */
  const momentum = createMomentum({ charisma, drive: () => heat.momentumScale });

  /**
   * The blazin tint. A flag nobody can see is a flag nobody will believe, so
   * the meter being spent has to show up on the fighter — for now as the rig
   * glowing, since there is no effects layer to hang anything better on.
   */
  let emissive = materials.map((m) => m.emissive?.clone() ?? null);
  function showBlazin(on) {
    materials.forEach((material, i) => {
      if (!material.emissive) return;
      if (on) material.emissive.setHex(BLAZIN_GLOW);
      else if (emissive[i]) material.emissive.copy(emissive[i]);
      material.emissiveIntensity = on ? 1 : 1;
    });
  }

  /**
   * Taunting pays on the way out, not on the way in.
   *
   * `taunt` is uninterruptible and four seconds long, which is the risk.
   * Paying only on `clipEnd` means a fighter knocked out of it gets nothing,
   * and that is the whole bargain: stand still in front of someone and get
   * paid for it, if you can.
   */
  machine.onChange(({ from, to, reason }) => {
    if (from !== 'taunt' || reason !== 'clipEnd') return;
    momentum.gain('taunt');
    // The same moment pays twice, into two different meters. Standing still in
    // front of a live opponent is both *doing well* and *worth watching*, and
    // it is the one action in the game that is purely the second.
    heat.record('taunt');
  });
  strikes.onWhiff(() => {
    exposed = FEEL.whiff.duration;
    // A whiff ends a chain outright. The cancel window is bought by connecting,
    // and there is nothing left to cancel out of.
    combo.missed();
  });

  /**
   * Which body this is, as the file actually drawn, and the latest change of
   * body still loading (or done).
   */
  let wearingUrl = fighter.source;
  /**
   * How far this body reaches, as measured against the mannequin `moves.json`
   * was taken on. The strike layer serves every move scaled by it, so the AI's
   * spacing, the whiff check and the rings all read this body's reach without
   * knowing there is more than one body.
   */
  let bodyReach = null;
  function measureBody() {
    bodyReach = reachOf(wearingUrl);
    strikes.setBodyReach(bodyReach);
    reach.setMoves(Object.fromEntries(strikes.names().map((name) => [name, strikes.get(name)])));
  }
  measureBody();
  let bodyReady = Promise.resolve(fighter.source);
  let bodyToken = 0;

  /**
   * Puts the fighter in a different body: `file` from `meshes.json`, or the
   * default for null.
   *
   * Not the retier case. Nothing can be swapped in place — the skeleton is the
   * body's own, so the clips are retargeted to it, the hitboxes and the hand a
   * weapon rides are its bones, grounding samples its skin, and the materials
   * the tint and the glow live on are its materials. So the new body is built
   * whole, detached, while the old one keeps fighting (or standing on its mark
   * behind the walkout), and then swapped in **one synchronous step**: attach
   * the new, dispose the old, re-point everything that was pointed at the old.
   * The pieces that are re-pointed are the same objects as before — the
   * animation binding, the hitbox and hurtbox volumes, the weapon model, this
   * group — so anything that took a reference to them at boot still holds the
   * right one.
   *
   * A failed load falls back to the default body, and to the mannequin if that
   * fails too; the slot is never left empty. Two bookings in quick succession
   * resolve to the later one: an older load that finishes second is thrown
   * away. Bodies and their retargeted clips are cached by URL, so fighting
   * the same opponent twice fetches and retargets nothing.
   */
  function wear(file) {
    if (!meshUrl || !defaultMesh) return Promise.resolve(wearingUrl);
    const wanted = meshUrl(file ?? defaultMesh);
    // Bumped even when there is nothing to load: asking for the body already
    // worn must still cancel a different one in flight. Without it, booking
    // Emeka and then somebody else before his body landed left Emeka's body
    // to arrive late and win.
    const token = ++bodyToken;
    if (wanted === wearingUrl) return (bodyReady = Promise.resolve(wearingUrl));
    const fallback = wanted === meshUrl(defaultMesh) ? fallbackUrl : meshUrl(defaultMesh);
    bodyReady = loadFighter({
      name: id,
      root: object3D,
      attach: false,
      modelUrl: wanted,
      fallbackUrl: fallback,
      animationUrls,
      onWarn,
    }).then((next) => {
      if (token !== bodyToken) {
        next.dispose();
        return wearingUrl;
      }
      changeBody(next);
      return wearingUrl;
    });
    return bodyReady;
  }

  function changeBody(next) {
    const previous = fighter;
    const oldMaterials = materials;
    next.attach();
    previous.dispose();
    for (const material of oldMaterials) material.dispose();
    fighter = next;
    wearingUrl = next.source;
    measureBody();

    materials = cloneMaterials(next.body);
    baseColours = materials.map((m) => m.color?.clone() ?? null);
    markGear(next.body);
    fitPads(next.body);
    dressed = materials.map((m) => dress(m));
    emissive = materials.map((m) => m.emissive?.clone() ?? null);
    applyTint(tintHex);
    applyGear(gearLooks);
    if (momentum.blazin) showBlazin(true);

    // Anything in flight belonged to the old body's clip and the old body's
    // timing; the new body starts in the state the fighter is in.
    if (strikes.active) strikes.cancel();
    animation.rebind(next);
    next.animator?.setTimeScale(1);
    hitboxes.rebind(next);
    hurtboxes.respec(hurtboxesOf(next.source));
    hurtboxes.rebind(next);
    hitboxes.update();
    hurtboxes.update();
    weaponModel.rebind(next);
    weaponModel.show(weapon);
    for (const listener of bodyListeners) listener({ source: wearingUrl, id });
  }
  const bodyListeners = new Set();

  const combatant = {
    id,
    /**
     * Metres every blow aimed at this body gains or loses against the
     * mannequin: a body line further back than its bones reach is further away.
     */
    get reachAgainst() {
      return bodyReach?.against ?? 0;
    },
    /** The current body's loader handle. A getter: it changes with `wear`. */
    get fighter() {
      return fighter;
    },
    machine,
    health,
    animation,
    strikes,
    combo,
    momentum,
    heat,
    hitboxes,
    hurtboxes,
    object3D,

    /** The debug wireframes, for the caller to add to the scene. */
    volumeViews: [hitboxes.object3D, hurtboxes.object3D],

    get position() {
      return object3D.position;
    },

    /**
     * Turns to face a point on the floor, ignoring any height difference.
     *
     * The rig's forward is its own -Z: that is the way the strike clip drives
     * the fist. three's `lookAt` aims an object's *+Z* at the target, so
     * facing someone is a lookAt followed by a half turn. Without it a fighter
     * would square up with their back to the opponent and punch the scenery.
     *
     * Whether this is called at all is `locomotion`'s decision, and the state
     * graph's: a fighter only turns in a state that declares `faces`.
     */
    faceTowards(point) {
      object3D.lookAt(new THREE.Vector3(point.x, object3D.position.y, point.z));
      object3D.rotateY(Math.PI);
      return combatant;
    },

    /**
     * Who this fighter is in with. Only heat asks, and only so that every event
     * in its list can name the fighter it happened against — a taunt or a run
     * of the same jab has an opponent without anybody passing one in.
     */
    get opponentId() {
      return opponentId;
    },
    /** The fighter opposite, when one was handed over. */
    get opponent() {
      return opponent;
    },
    squareUpWith(other) {
      opponent = other && typeof other === 'object' ? other : null;
      opponentId = other?.id ?? other ?? null;
      return combatant;
    },

    /**
     * Why `which` cannot be thrown for want of a reeling target, or null.
     *
     * Exposed so the debug overlay can dim a button rather than let it silently
     * do nothing — the state buttons have carried their own refusal since step
     * 5, and a move button that looks live and is not is worse than one that
     * looks dead.
     */
    stunRefusal: (which) => stunRefusal(strikes.get(which)),

    /**
     * The direction the fighter is actually pointed, on the floor, as plain
     * `{ x, z }` — their own -Z.
     *
     * This is not the same thing as the direction *to* the opponent, and the
     * gap between the two is the whole of the facing rule: a fighter mid-punch
     * is aimed where they committed, while their opponent may have walked out
     * of it. Knockback and reach both read this rather than re-deriving from
     * positions, because a punch travels the way the fist is going.
     */
    get facing() {
      object3D.updateMatrixWorld();
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(object3D.quaternion);
      const flat = Math.hypot(forward.x, forward.z) || 1;
      return { x: forward.x / flat, z: forward.z / flat };
    },

    /**
     * Throws a strike. The state machine is the authority on whether the
     * fighter may — mid-stagger or mid-punch it refuses, and the move never
     * starts — so an illegal attack surfaces here rather than quietly working.
     */
    throwStrike(which, { cancel = false } = {}) {
      const move = strikes.get(which);
      // A fighter can only throw what their styles taught them — or the one
      // thing the weapon in their hands does. This is the only gate on it:
      // everything downstream would happily run a move from a style nobody
      // equipped, or swing a bat nobody is holding.
      if (!move || !combatant.knows(which)) return false;

      // A move that wants a reeling target is refused at every door, not just
      // the one the player presses. See `stunRefusal`.
      if (stunRefusal(move)) return false;

      if (strikes.active && !cancel) {
        // Too late to throw it, but it may be a legal follow-up in a few
        // frames. Remembering it here rather than in the control layer means a
        // player and a timer both get input buffering for nothing.
        combo.want(which);
        return false;
      }

      // The move brings its own animation. Handed over *before* the transition
      // is asked for, so the clip arrives with the state change instead of a
      // frame behind it — every strike runs in the same state and they must
      // not all look like the same right hand.
      animation.nextClip(move.clip);

      // The move names the state it runs in, rather than every caller knowing
      // that a punch means `strike`. A cancel is allowed to cut the current
      // state short; anything else has to wait its turn.
      if (!machine.request(move.state, { ignoreInterrupt: cancel })) {
        // Refused: the clip must not be left waiting to ambush the next entry.
        animation.nextClip(null);
        return false;
      }
      // Re-entering `strike` does not trip the leave-a-strike handler below, so
      // the cancelled move is dropped here instead.
      if (strikes.active) strikes.cancel();

      const started = strikes.start(which, {
        clipDuration: fighter.animator?.duration(move.clip),
        speed: loadout.stats.speed,
      });
      // On the throw, not on the hit: a fighter who has found one thing that
      // works is dull whether or not it is connecting.
      if (started) heat.threw(which, { opponent: opponentId });

      /**
       * And the weapon wears out, also on the throw.
       *
       * The swing that empties it still happens — a bat breaks *on* the last
       * blow, not instead of it — and the fighter is empty-handed the moment
       * after. Disarming mid-swing is safe: the strike layer checks `knows()`
       * when a move starts and never again, so the move it is already running
       * plays out whole.
       */
      if (started && move.weapon && weapon && !weapon.broken) {
        weapon.swing();
        if (weapon.broken) {
          const broken = weapon;
          weapon = null;
          broken.heldBy(null);
          weaponModel.show(null);
          for (const listener of armListeners) {
            listener({ weapon: null, previous: broken, broke: true, id });
          }
        }
      }
      // Set *after* starting, because the scale is derived from the move's
      // frame data and the clip's real length, and only the layer knows both.
      if (started) fighter.animator?.setTimeScale(strikes.timeScale);
      return started;
    },

    /**
     * Throws a clinch attempt.
     *
     * It goes out exactly the way a punch does — same layer, same frame data,
     * same active window — because it is thrown the same way. What differs is
     * what happens when it connects, and that is `fight.js`'s business.
     */
    throwGrapple(which = 'clinch') {
      const move = strikes.get(which);
      if (!move || move.kind !== 'grapple') return false;
      return combatant.throwStrike(which);
    },

    /** What this fighter knows and is worth. Read by `fight.js` on every blow. */
    get loadout() {
      return loadout;
    },

    /**
     * Every move this fighter can throw right now.
     *
     * **Holding a weapon swaps the strike set for the weapon's one attack.** A
     * bat is not a punch you also have; it is what your hands are doing. What
     * survives is the grapples: one hand is holding a bat, and the other can
     * still grab somebody — and losing the clinch would take the whip, the wall
     * slam and the escape with it.
     */
    get moves() {
      if (!weapon || weapon.broken) return loadout.moves;
      return [weapon.move, ...loadout.moves.filter((name) => isGrapple(name))];
    },
    knows(name) {
      if (weapon && !weapon.broken) {
        return name === weapon.move || (isGrapple(name) && loadout.knows(name));
      }
      return loadout.knows(name);
    },
    /** Follow-ups of a move, filtered to this fighter's own loadout. */
    follows: (name) => loadout.follows(name).filter((next) => combatant.knows(next)),

    // --- weapons --------------------------------------------------------------

    /** What is in their hands, or null. */
    get weapon() {
      return weapon;
    },
    get armed() {
      return Boolean(weapon) && !weapon.broken;
    },

    /**
     * Puts a weapon in their hands, and takes whatever was there out.
     *
     * Returns what they were holding, so a caller that cares — a reversal,
     * which hands a weapon over rather than conjuring one — can put it
     * somewhere. Anything in flight is dropped: a fighter who has just been
     * handed a bat should not still be finishing a jab with it.
     */
    arm(next) {
      const previous = weapon;
      previous?.heldBy(null);
      weapon = next ?? null;
      weapon?.heldBy(combatant);
      weaponModel.show(weapon);
      strikes.cancel();
      combo.reset();
      for (const listener of armListeners) listener({ weapon, previous, id: id });
      return previous;
    },
    /** Empty-handed again. Returns what they were holding. */
    disarm() {
      return combatant.arm(null);
    },
    /** Fires whenever what is in their hands changes. */
    onArm(listener) {
      armListeners.add(listener);
      return () => armListeners.delete(listener);
    },

    /**
     * Swaps styles mid-round. Drops anything in flight, because a fighter who
     * has just become a wrestler should not still be finishing a spin kick.
     * Throws on an impossible loadout rather than silently keeping the old one.
     */
    setStyles(names) {
      loadout = createLoadout(names, { spread });
      strikes.cancel();
      combo.reset();
      return loadout;
    },

    /**
     * This fighter's own deviation from their styles, or null for none.
     *
     * Kept on the combatant rather than passed to `setStyles`, so swapping
     * styles from the overlay cannot silently wipe it — the two are separate
     * facts about a fighter and changing one must not clear the other.
     */
    get spread() {
      return spread;
    },
    setSpread(next) {
      spread = next ?? null;
      loadout = createLoadout(loadout.ids, { spread });
      return loadout.stats;
    },

    /**
     * How fast the meter fills for them. The base they were built with, times
     * whatever their gear is worth to the crowd tonight (`bank.charisma()`).
     */
    get charisma() {
      return momentum.charisma;
    },
    setCharisma(next) {
      return momentum.setCharisma(next);
    },

    /** The body: the file actually drawn, and how to change it. */
    get wearing() {
      return wearingUrl;
    },
    /** Resolves when the latest `wear` has landed, with the file drawn. */
    get bodyReady() {
      return bodyReady;
    },
    wear,
    /**
     * Warms a body this slot may be asked to wear — a card on the table —
     * without wearing it. `file` is a `meshes.json` file name, as `wear` takes.
     */
    preload(file = null) {
      if (!meshUrl) return Promise.resolve(false);
      return preloadFighter({ modelUrl: meshUrl(file ?? defaultMesh), animationUrls, onWarn: () => {} });
    },
    /** Fires after every change of body. */
    onBody(listener) {
      bodyListeners.add(listener);
      return () => bodyListeners.delete(listener);
    },

    /** The colour they wear, as a hex number, or null for the file's own. */
    get tint() {
      return tintHex;
    },
    setTint(next) {
      return applyTint(next);
    },

    /**
     * What they wear where the camera can see it: `{ hands, feet, shins }`,
     * each a tier's `look` or null (`bank.looksOf`). Painted, not modelled.
     */
    get gear() {
      return { ...gearLooks };
    },
    setGear(next) {
      return applyGear(next ?? {});
    },

    /** What to call them. See `name` above: this is not the id. */
    get name() {
      return name;
    },
    setName(next) {
      name = next || id;
      return name;
    },

    /** The plain-data view `strikeLayer` resolves against. */
    asTarget() {
      return { id, hurtboxes: hurtboxes.list };
    },

    /**
     * Seconds this fighter is still reeling off a hard surface.
     *
     * A separate window from `vulnerable`, and not a multiplier on anything:
     * it does not change what a blow is worth, it changes what is *available*.
     * Only inside it can an opponent take the finisher off a wall.
     */
    get stunned() {
      return reeling > 0;
    },
    get stunnedFor() {
      return reeling;
    },
    /** Set by `fight.js` when this fighter arrives at something solid. */
    stun(seconds) {
      if (seconds > reeling) reeling = seconds;
      return reeling;
    },
    clearStun() {
      reeling = 0;
    },

    /** True while a missed strike has this fighter open to a punish. */
    get vulnerable() {
      return exposed > 0;
    },
    /** Seconds left on that window, for the debug overlay. */
    get vulnerableFor() {
      return exposed;
    },
    /** What incoming damage is multiplied by right now. `fight.js` reads this. */
    get vulnerability() {
      return exposed > 0 ? FEEL.whiff.damageScale : 1;
    },
    /** Spends the window. Called by `fight.js` once a blow has cashed it in. */
    consumeVulnerability() {
      exposed = 0;
    },

    /** True while this fighter is blazin. Read by `fight.js` on every hit. */
    get blazin() {
      return momentum.blazin;
    },

    /** Spends a full meter. Refused at anything less, which is the whole cost. */
    ignite() {
      const lit = momentum.ignite();
      if (lit) showBlazin(true);
      return lit;
    },

    /** Stands the fighter down, tint and all. */
    taunt() {
      return machine.request('taunt');
    },

    update(dt) {
      if (exposed > 0) exposed = Math.max(0, exposed - dt);
      if (reeling > 0) reeling = Math.max(0, reeling - dt);

      // Backing away is the one heat source that is a duration rather than a
      // moment, and `walkBack` is exactly it: the state graph already knows the
      // difference between retreating and circling, so nothing here has to.
      heat.update(dt, { retreating: machine.state === 'walkBack' });

      const wasBlazin = momentum.blazin;
      momentum.update(dt);
      // The meter runs its own clock; the tint just follows it out.
      if (wasBlazin && !momentum.blazin) showBlazin(false);

      // A buffered follow-up fires the first frame it becomes legal, which is
      // usually the frame the previous blow landed. Done here rather than in a
      // control layer because buffering is a property of the fighter, not of
      // whoever is driving them.
      combo.update(dt);
      const queued = combo.take();
      if (queued && combatant.throwStrike(queued, { cancel: true })) combo.commit();

      animation.update(dt);
      health.update(dt);
      hitboxes.update();
      hurtboxes.update();
      hitboxes.setLive(
        strikes.windowOpen ? (strikes.get(strikes.strike)?.hitbox ?? null) : null
      );
    },

    setDebugVisible(next) {
      hitboxes.setVisible(next);
      hurtboxes.setVisible(next);
    },

    reach,
    get reachVisible() {
      return reach.visible;
    },
    setReachVisible(next) {
      return reach.setVisible(next);
    },

    dispose() {
      weaponModel.dispose();
      animation.dispose();
      hitboxes.dispose();
      hurtboxes.dispose();
      reach.dispose();
      bodyToken += 1;
      fighter.dispose();
      object3D.removeFromParent();
    },
  };

  return combatant;
}

/**
 * Gives a loaded model its own materials, and hands them back.
 *
 * Two fighters built from the same GLB share one material otherwise, so
 * colouring the second would silently recolour the first — and, since step 18,
 * so would setting one of them alight.
 */
function cloneMaterials(object3D) {
  const materials = [];
  object3D.traverse((child) => {
    if (!child.isMesh || !child.material) return;
    child.material = child.material.clone();
    materials.push(child.material);
  });
  return materials;
}
